/**
 * src/services/interestApi.ts
 *
 * File-level:
 * - Client for expressed-interest related backend endpoints.
 * - Always builds absolute URLs using apiBase() to avoid calling the frontend origin by mistake.
 *
 * Notes:
 * - Functions return normalized shapes or throw an Error with a clear message.
 */

import { apiBase } from '@/utils/api';

/**
 * Build an absolute URL for the interest-records endpoints.
 *
 * @param path string - path after /interest-records (e.g. '/count/:id')
 * @returns string - absolute URL
 */
function buildUrl(path: string): string {
  const base = String(apiBase?.() ?? (window as any).__API_BASE__ ?? '').replace(/\/$/, '');
  // Fallback: if apiBase is not set, use relative path (helps some dev scenarios)
  if (!base) {
    return `/api/interest-records${path}`;
  }
  return `${base.replace(/\/api$/, '')}/api/interest-records${path}`;
}

/**
 * Normalize non-2xx responses into a thrown Error with helpful message.
 *
 * @param res Response
 */
async function handleResponse(res: Response) {
  const text = await res.text();
  let body: any = text;
  try {
    body = text ? JSON.parse(text) : text;
  } catch {
    // leave as text
  }
  if (!res.ok) {
    const err = new Error(`Failed (${res.status}) ${res.statusText}: ${typeof body === 'object' ? JSON.stringify(body) : body}`);
    (err as any).status = res.status;
    (err as any).body = body;
    throw err;
  }
  return body;
}

/**
 * getExpressedInterestCount
 *
 * @param toUserId string - recipient userId (logged-in user)
 * @returns Promise<{ success: boolean, count: number }>
 */
export async function getExpressedInterestCount(toUserId: string): Promise<{ success?: boolean; count: number }> {
  if (!toUserId) {
    throw new Error('toUserId is required');
  }
  const url = buildUrl(`/count/${encodeURIComponent(toUserId)}`);
  const res = await fetch(url, { method: 'GET', credentials: 'include' });
  const parsed = await handleResponse(res);
  // Backend may return { success: true, count } or { count } or {success:false}
  const count = Number(parsed?.count ?? parsed?.data?.count ?? 0);
  return { success: parsed?.success ?? true, count: isNaN(count) ? 0 : count };
}

/**
 * Options for getExpressedInterestList
 */
export interface InterestListOpts {
  status?: string;
  limit?: number;
  page?: number;
}

/**
 * getExpressedInterestList
 *
 * @param toUserId string - recipient userId (logged-in user)
 * @param opts InterestListOpts
 * @returns Promise<{ success?: boolean; list: any[] }>
 */
export async function getExpressedInterestList(
  toUserId: string,
  opts: InterestListOpts = {}
): Promise<{ success?: boolean; list: any[] }> {
  if (!toUserId) {
    throw new Error('toUserId is required');
  }

  const params = new URLSearchParams();
  if (opts.status) params.append('status', opts.status);
  if (opts.limit) params.append('limit', String(opts.limit));
  if (opts.page) params.append('page', String(opts.page));
  const query = params.toString() ? `?${params.toString()}` : '';
  const url = buildUrl(`/list/${encodeURIComponent(toUserId)}${query}`);

  console.log('📬 [INTEREST-LIST] Fetching from:', url);

  try {
    const res = await fetch(url, { 
      method: 'GET', 
      credentials: 'include' 
    });

    const parsed = await handleResponse(res);
    console.log('📬 [INTEREST-LIST] Response:', parsed);

    // Handle different response structures
    let list: any[] = [];

    if (Array.isArray(parsed)) {
      // Response is directly an array
      list = parsed;
    } else if (Array.isArray(parsed?.list)) {
      // Response has { list: [...] }
      list = parsed.list;
    } else if (Array.isArray(parsed?.data?.list)) {
      // Response has { data: { list: [...] } }
      list = parsed.data.list;
    } else if (Array.isArray(parsed?.data)) {
      // Response has { data: [...] }
      list = parsed.data;
    } else if (Array.isArray(parsed?.interests)) {
      // Response has { interests: [...] }
      list = parsed.interests;
    } else {
      console.warn('⚠️ [INTEREST-LIST] Unexpected response structure, returning empty array');
      list = [];
    }

    return { 
      success: parsed?.success ?? true, 
      list 
    };

  } catch (error: any) {
    console.error('❌ [INTEREST-LIST] Error:', error);
    // Return empty list instead of throwing, for better UX
    return { 
      success: false, 
      list: [] 
    };
  }
}

/**
 * postInterestAction
 *
 * @param id number - interest record id
 * @param action 'Accept'|'Reject'
 * @param actorId string - the acting userId (recipient who is Accepting/Rejecting)
 * @returns Promise<any>
 */
export async function postInterestAction(id: number, action: 'Accept' | 'Reject', actorId?: string): Promise<any> {
  if (!id) throw new Error('id is required');
  if (!action || (action !== 'Accept' && action !== 'Reject')) throw new Error('action must be Accept or Reject');

  const url = buildUrl('/action');
  const body = { id, action, actorId };
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  const parsed = await handleResponse(res);
  return parsed;
}

/**
 * getInterestHealth
 *
 * Quick health check against the interest-records fallback endpoint if present.
 */
export async function getInterestHealth(): Promise<any> {
  const url = buildUrl('/health');
  const res = await fetch(url, { method: 'GET', credentials: 'include' });
  const parsed = await handleResponse(res);
  return parsed;
}